package com.pincer.core.model

import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive

/*
Schema versions for the Pincer document.

Every Pincer carries a schemaVersion, and every stored patch is stamped with the version it was written in
(StringStore.writePincerPatch). Anything without a version is from before September 2026 and counts as 1.

When the shape of the document changes, add a Migration that takes both documents and patches from the
previous version to the next. Stored data is never rewritten: old snapshots are migrated as they're parsed,
and old patches as they're applied, so loading a pincer can replay history written in any version.

No migrations yet. The first expected one is `selected` becoming a history of selections (event_space).
*/

const val SCHEMA_VERSION_KEY = "schemaVersion"

class Migration(
    val toVersion : Int,
    val document  : (JsonObject) -> JsonObject,
    val patch     : (JsonObject) -> JsonObject,
)

class Schema(val migrations: List<Migration>) {

    val current: Int = migrations.maxOfOrNull { it.toVersion } ?: 1

    fun versionOf(json: JsonObject): Int = json[SCHEMA_VERSION_KEY]?.jsonPrimitive?.intOrNull ?: 1

    fun migrateDocument(json: JsonObject) = migrate(json) { migration, it -> migration.document(it) }

    fun migratePatch(json: JsonObject) = migrate(json) { migration, it -> migration.patch(it) }

    /** Stamps the current version onto a patch that's about to be stored. */
    fun stamp(json: JsonObject) = JsonObject(json + (SCHEMA_VERSION_KEY to JsonPrimitive(current)))

    private fun migrate(json: JsonObject, step: (Migration, JsonObject) -> JsonObject): JsonObject {
        val from = versionOf(json)
        if (from == current) return json
        if (from > current) throw IllegalStateException("Schema version $from is newer than this code ($current)")
        var build = json
        for (migration in migrations.sortedBy { it.toVersion }) {
            if (migration.toVersion > from) build = step(migration, build)
        }
        return stamp(build)
    }

    companion object {
        val pincer = Schema(migrations = listOf())
    }

}
